/** Small pieces shared by the corporate client screens. */
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Building2 } from "lucide-react";
import { CONTRACT_STATE_LABEL, corporateApi, type ContractState } from "@/api/corporate.api";
import { usePermissions } from "@/hooks/use-permissions";
import { cn } from "@/lib/utils";

const TONE: Record<ContractState, string> = {
  active: "bg-[#e6f0ee] text-[#004952]",
  ending: "bg-[#fff1d6] text-[#7a5600]",
  not_started: "bg-[#fff1d6] text-[#7a5600]",
  expired: "bg-[#ffe9e1] text-[#c4532f]",
  none: "bg-[#f1ece2] text-[#4d6669]",
};

export function ContractPill({ state }: { state: ContractState }) {
  return <span className={cn("inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold", TONE[state])}>{CONTRACT_STATE_LABEL[state]}</span>;
}

/** "2026-03-15…" → "15 Mar 2026". */
export function formatDay(iso?: string | null): string {
  if (!iso) return "";
  const d = new Date(iso.slice(0, 10) + "T00:00:00");
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

export function todayDay(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** "Paid by GCX" pill for one patient, or nothing when they pay privately. */
export function PaidByBadge({ code, name, clientId, className }: { code: string; name: string; clientId?: string; className?: string }) {
  const cls = cn(
    "inline-flex items-center gap-1 rounded-full bg-[#e6f0ee] px-2.5 py-0.5 text-xs font-semibold text-[#004952] dark:bg-muted dark:text-primary",
    className,
  );
  const body = <><Building2 className="h-3 w-3" />Paid by {code}</>;
  return clientId ? (
    <Link to={`/corporate/${clientId}`} title={name} className={cn(cls, "hover:underline")} onClick={(e) => e.stopPropagation()}>{body}</Link>
  ) : (
    <span title={name} className={cls}>{body}</span>
  );
}

/** Clinic-wide patient → company map for list screens (empty without access). */
export function useCoverageMap() {
  const { can } = usePermissions();
  const enabled = can("corporate_clients", "list");
  const q = useQuery({ queryKey: ["corporate", "coverage-map"], queryFn: corporateApi.coverageMap, enabled, staleTime: 60_000 });
  const map = new Map((q.data ?? []).map((e) => [e.patient_id, e]));
  return { map, enabled };
}

/** One patient's cover, allowance and any booking warning. */
export function useCoverage(patientId?: string | null) {
  const { can } = usePermissions();
  return useQuery({
    queryKey: ["corporate", "coverage", patientId],
    queryFn: () => corporateApi.coverage(patientId!),
    enabled: Boolean(patientId) && can("corporate_clients", "read"),
    staleTime: 30_000,
  });
}

/** Chart side card: who pays and how many sessions are left this year. */
export function CoverageCard({ patientId }: { patientId: string }) {
  const q = useCoverage(patientId);
  const c = q.data;
  if (!c?.covered || !c.client) return null;
  return (
    <div className={cn("mt-3 rounded-2xl px-3 py-2.5 text-xs", c.warning ? "bg-[#fff1d6] text-[#7a5600]" : "bg-[#e6f0ee] text-[#003a43] dark:bg-muted dark:text-foreground")}>
      <Link to={`/corporate/${c.client.id}`} className="flex items-center gap-1.5 font-semibold hover:underline">
        <Building2 className="h-3.5 w-3.5" />Paid by {c.client.name}
      </Link>
      <p className="mt-0.5">
        {c.allowance != null
          ? `${c.left ?? 0} of ${c.allowance} sessions left this year${c.booked ? ` (${c.booked} booked)` : ""}`
          : `${c.used} session${c.used === 1 ? "" : "s"} this year · no limit`}
      </p>
      {c.warning && <p className="mt-1 font-semibold">{c.warning}</p>}
    </div>
  );
}

/** Booking notice: covered by a company, or the company won't pay this one. */
export function CoverageNotice({ patientId }: { patientId?: string | null }) {
  const q = useCoverage(patientId);
  const c = q.data;
  if (!c?.covered || !c.client) return null;
  return (
    <div className={cn("flex items-start gap-2 rounded-2xl px-4 py-3 text-sm", c.warning ? "bg-[#fff1d6] text-[#7a5600]" : "bg-[#e6f0ee] text-[#003a43] dark:bg-muted dark:text-foreground")}>
      <Building2 className="mt-0.5 h-4 w-4 shrink-0" />
      <p>
        <span className="font-semibold">Paid by {c.client.name}.</span>{" "}
        {c.warning ||
          (c.allowance != null
            ? `${c.left ?? 0} of ${c.allowance} sessions left this contract year.`
            : "No session limit on this contract.")}
      </p>
    </div>
  );
}
